import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Building2, Loader2 } from 'lucide-react';
import { organisationsAPI } from '../../services/api';
import toast from '../../services/toast';
import { AuthSplitScaffold, AuthSplitShell } from '../../components/auth/AuthSplitShell';
import Input from '../../components/ui/Input';

const SetupOrganisation = () => {
  const [organisation, setOrganisation] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting },
  } = useForm({ defaultValues: { name: '', githubOrgLogin: '', githubToken: '' } });

  const nameW = watch('name');
  const loginW = watch('githubOrgLogin');
  const tokenW = watch('githubToken');

  useEffect(() => {
    organisationsAPI
      .list()
      .then((list) => {
        const org = list && list.length > 0 ? list[0] : null;
        setOrganisation(org);
        if (org) {
          reset({
            name: org.name || '',
            githubOrgLogin: org.githubOrgLogin || '',
            githubToken: '',
          });
        }
      })
      .catch((err) => {
        toast.error((err as Error).message || 'Failed to load organisation');
        setOrganisation(null);
      })
      .finally(() => setLoading(false));
  }, [reset]);

  const onSubmit = handleSubmit(async ({ name, githubOrgLogin, githubToken }) => {
    const nameTrim = String(name).trim();
    const loginTrim = String(githubOrgLogin).trim();
    const tokenTrim = String(githubToken).trim();
    if (!organisation) return;
    if (!nameTrim) {
      toast.error('Organisation name is required');
      return;
    }
    if (!loginTrim) {
      toast.error('GitHub organisation name is required');
      return;
    }
    if (!tokenTrim) {
      toast.error('GitHub access token is required');
      return;
    }
    try {
      await organisationsAPI.update(organisation.id, { name: nameTrim });
      await organisationsAPI.updateGitHubIntegration(organisation.id, {
        githubOrgLogin: loginTrim,
        githubToken: tokenTrim,
      });
      try {
        await organisationsAPI.refreshContributors(organisation.id);
      } catch {
        /* non-blocking */
      }
      toast.success('Setup complete');
      navigate('/', { replace: true });
    } catch (err) {
      toast.error((err as Error).message || 'Failed to save');
    }
  });

  if (loading) {
    return (
      <AuthSplitScaffold>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-vivid-red" aria-hidden />
        </div>
      </AuthSplitScaffold>
    );
  }

  if (!organisation) {
    return (
      <AuthSplitScaffold>
        <div className="text-center text-slate-400 py-8">
          <p>No organisation found. Please try again or contact support.</p>
          <button type="button" onClick={() => navigate('/')} className="mt-6 btn-primary-mdl">
            Go to dashboard
          </button>
        </div>
      </AuthSplitScaffold>
    );
  }

  return (
    <AuthSplitShell
      title="Create your organisation"
      description="Set your organisation name and details to get started"
    >
      <form className="space-y-5" onSubmit={onSubmit} noValidate>
        <div className="space-y-4">
          <div>
            <label htmlFor="org-name" className="block text-sm font-medium text-slate-300 mb-1.5">
              Organisation name
            </label>
            <Input
              id="org-name"
              type="text"
              placeholder="e.g. Acme Inc"
              {...register('name', { required: true })}
            />
          </div>
          <div>
            <label
              htmlFor="github-org-login"
              className="block text-sm font-medium text-slate-300 mb-1.5"
            >
              GitHub organisation name
            </label>
            <Input
              id="github-org-login"
              type="text"
              autoComplete="off"
              placeholder="e.g. my-company"
              {...register('githubOrgLogin', { required: true })}
            />
            <p className="mt-1 text-xs text-slate-400">
              Your GitHub org username (required for repo discovery and linking members).
            </p>
          </div>
          <div>
            <label
              htmlFor="github-token"
              className="block text-sm font-medium text-slate-300 mb-1.5"
            >
              GitHub access token
            </label>
            <Input
              id="github-token"
              type="password"
              autoComplete="off"
              placeholder="ghp_... or fine-grained token"
              {...register('githubToken', { required: true })}
            />
            <p className="mt-1 text-xs text-slate-400">
              Required for repo discovery, connecting repositories, and syncing. Create one in
              GitHub Settings → Developer settings.
            </p>
          </div>
        </div>
        <button
          type="submit"
          disabled={
            isSubmitting ||
            !String(nameW || '').trim() ||
            !String(loginW || '').trim() ||
            !String(tokenW || '').trim()
          }
          className="btn-primary-mdl w-full flex items-center justify-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden />
              Saving...
            </>
          ) : (
            <>
              <Building2 className="w-4 h-4 shrink-0" aria-hidden />
              Continue
            </>
          )}
        </button>
      </form>
    </AuthSplitShell>
  );
};

export default SetupOrganisation;
